import * as React from 'react';
import { useState, useEffect } from 'react';
import { useAppNavigation } from "../../hooks/useAppNavigation";
import SubPages from "../../pages/subpages/SubPagesEnum";
import { MyModelsPageState } from '../../pages/subpages/MyModels';
import { MyModelMetadata } from "../../pages/subpages/MyModels";
import apiService from '../../services/ApiService';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import "../style/ModelFolder.css";
import ModelListItemOptionMenu from './ModelListItemOptionMenu';
import { useAppStore } from '../../shared/stores/useAppStore';
import TailwindModal from '../Tailwind/TailwindModal';
import TailwindButton from '../Tailwind/TailwindButton';
import { cn } from '../../lib/utils';

const deleteModal = (model: MyModelMetadata, state: MyModelsPageState, reloadState: Function) => {
    const parentElem = state.folders[model.folderId.toString()]

    const handleClose = () => {
        model.deleteModalOpen = false;
        reloadState();
    }

    const handleDelete = () => {
        apiService.deleteModel(model.id).then(response => {
            if (response) {
                model.deleteModalOpen = false;
                const index = parentElem.modelChildrensIds.indexOf(model.id);
                if (index !== -1) {
                    parentElem.modelChildrensIds.splice(index, 1);
                }
                delete state.models[model.id.toString()]
                useAppStore.getState().incrementModelsRemaining();
                reloadState();
            }
        })
    }

    return (
        <TailwindModal
            isOpen={model.deleteModalOpen}
            onClose={handleClose}
            title="Are you sure?"
            size="sm"
            footer={
                <div className="flex justify-center gap-3 w-full">
                    <TailwindButton variant="ghost" icon="close" size="sm" onClick={handleClose}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="danger" icon="delete_forever" size="sm" onClick={handleDelete}>
                        Yes, Delete
                    </TailwindButton>
                </div>
            }
        >
            <p className="text-base text-center text-slate-700">
                This will delete <b>{model.name}</b> and all its content permanently.
            </p>
        </TailwindModal>
    );
}

const leaveModal = (model: MyModelMetadata, state: MyModelsPageState, reloadState: Function) => {
    const handleClose = () => {
        model.deleteModalOpen = false;
        reloadState();
    }

    const handleLeave = () => {
        apiService.leaveSharedModels(model.id).then(async response => {
            if (response) {
                if (response.ok) {
                    const folderSharedModelId = await response.json()
                    const parentElem = state.folders[folderSharedModelId.toString()]
                    model.deleteModalOpen = false;
                    const index = parentElem.modelChildrensIds.indexOf(model.id);
                    if (index !== -1) {
                        parentElem.modelChildrensIds.splice(index, 1);
                    }
                    delete state.models[model.id.toString()]
                    reloadState();
                }
            }
        })
    }

    return (
        <TailwindModal
            isOpen={model.deleteModalOpen}
            onClose={handleClose}
            title="Are you sure?"
            size="sm"
            footer={
                <div className="flex justify-center gap-3 w-full">
                    <TailwindButton variant="ghost" icon="close" size="sm" onClick={handleClose}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="danger" icon="exit_to_app" size="sm" onClick={handleLeave}>
                        Yes, leave sharing
                    </TailwindButton>
                </div>
            }
        >
            <p className="text-base text-center text-slate-700">
                This will delete <b>{model.name}</b> from the shared models.
            </p>
        </TailwindModal>
    );
}

function RenameForm({ model, reloadState, isSharedContext }: { model: MyModelMetadata, reloadState: Function, isSharedContext: boolean }) {
    const [name, setName] = useState(model.name);
    const [error, setError] = useState(false);

    const onSubmit = (event) => {
        event.preventDefault();
        if (!name.trim()) {
            setError(true);
            return;
        }
        if (model.id) {
            const putApi = isSharedContext
                ? apiService.putModelSharedModels(model.id, model.folderId)
                : apiService.putModel(model.id, name, model.folderId);

            putApi.then(response => {
                if (response) {
                    if (!isSharedContext) model.name = name;
                    model.editing = false;
                    reloadState();
                }
            });
        }
    };

    return (
        <div className="px-3 pb-2">
            <form onSubmit={onSubmit}>
                <div className="relative flex items-center gap-1">
                    <input
                        type="text"
                        value={name}
                        className={cn(
                            "flex-1 min-w-0 border-b bg-transparent text-sm text-slate-900 focus:outline-none focus:border-primary transition-colors",
                            error ? "border-red-400" : "border-slate-300"
                        )}
                        onChange={(e) => { setName(e.target.value); if (error) setError(false); }}
                        onFocus={event => { event.target.select(); }}
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="size-7 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors flex items-center justify-center"
                    >
                        <span className="material-symbols-outlined text-[18px]">done</span>
                    </button>
                    <button
                        type="button"
                        className="size-7 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors flex items-center justify-center"
                        onClick={() => { model.editing = false; reloadState(); }}
                    >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                    {error && (
                        <p className="absolute bottom-full left-0 mb-1 text-xs text-red-500 bg-white border border-red-100 rounded px-1.5 py-0.5 shadow-sm whitespace-nowrap">
                            Name cannot be empty
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
}

export default function ModelGridItem({ model, state, reloadState, isSharedContext = false, stateMyModels = null }) {
    const navigateToPage = useAppNavigation();
    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: 'ELEM',
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
        item: { ...model, isSharedContext, type: 'MODEL' }
      }),
       [model, isSharedContext]
      )

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

    const [userRights, setUserRights] = useState({
        user_id: 0,
        right_to_share: false,
        right_to_read: false,
        right_to_write: false,
        right_to_duplicate: false,
    })

    useEffect(() => {
        if (isSharedContext) {
            apiService.getUserRightsSharedModels(model.id).then(async response => {
                if (response) {
                    if (response.ok) {
                        const elem = await response.json()
                        setUserRights({
                            user_id: elem.user_id,
                            right_to_share: elem.right_to_share,
                            right_to_read: elem.right_to_read,
                            right_to_write: elem.right_to_write,
                            right_to_duplicate: elem.right_to_duplicate,
                        })
                    }
                }
            })
        }
    }, []);

    return (
        <div ref={(model.editing) ? null : drag} style={{ cursor: 'move', opacity: isDragging ? 0.5 : 1 }}>
            <div className="w-full h-[180px]">
                <div
                    className="block text-left w-full h-full border-none bg-transparent"
                    onDoubleClick={() => {
                        if (!model.editing && (!isSharedContext || userRights.right_to_read) && model.modelRulesDeducted?.is_active !== false) {
                            if (model.isNew) {
                                navigateToPage(SubPages.NewModel, model);
                            }
                            else {
                                navigateToPage(SubPages.FinancialModelEditor, model);
                            }
                        }
                    }}
                >
                    <div
                        className={cn(
                            "models-item-component card-hover flex flex-col w-full h-full border border-slate-100 bg-white rounded-xl",
                            "transition-all group"
                        )}
                    >
                        <div className="flex items-center px-2 pt-2">
                            {isSharedContext ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                    <span className="material-symbols-outlined text-[12px]">group</span>
                                    Shared with me
                                </span>
                            ) : (
                                <div className="flex items-center gap-1">
                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-white bg-slate-900 px-1.5 py-0.5 rounded">
                                        <span className="material-symbols-outlined text-[12px]">shield_person</span>
                                        Owner
                                    </span>
                                    {model.isShared && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                            <span className="material-symbols-outlined text-[12px]">share</span>
                                            Shared
                                        </span>
                                    )}
                                </div>
                            )}
                            <div className="flex-grow"/>
                            <button
                                className="size-7 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors flex items-center justify-center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuAnchorEl(e.currentTarget);
                                }}
                            >
                                <span className="material-symbols-outlined text-[18px]">more_vert</span>
                            </button>
                        </div>

                        <div className="flex-1 flex items-center justify-center">
                            <div className="size-14 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors shadow-sm">
                                <span className="material-symbols-outlined text-[32px]">architecture</span>
                            </div>
                        </div>

                        {/* Quick action buttons */}
                        <div className="flex items-center justify-center gap-1 px-3 py-1">
                            <div className="relative group/fin">
                                <button
                                    className={cn(
                                        "size-7 rounded-lg transition-colors flex items-center justify-center",
                                        !model.isNew && model.modelRulesDeducted?.is_active !== false
                                            ? "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                            : "opacity-40 cursor-not-allowed text-slate-300"
                                    )}
                                    disabled={model.modelRulesDeducted?.is_active === false}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isSharedContext || userRights.right_to_read) {
                                            if (model.isNew) {
                                                navigateToPage(SubPages.NewModel, model);
                                            } else {
                                                navigateToPage(SubPages.FinancialModelEditor, model);
                                            }
                                        }
                                    }}
                                    aria-label="Open Financial Editor"
                                >
                                    <span className="material-symbols-outlined text-[18px]">monitoring</span>
                                </button>
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[11px] rounded opacity-0 group-hover/fin:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-10">
                                    {model.modelRulesDeducted?.is_active === false ? 'Plan expired' : 'Financial Editor'}
                                </div>
                            </div>
                            <div className="relative group/ope">
                                <button
                                    className={cn(
                                        "size-7 rounded-lg transition-colors flex items-center justify-center",
                                        !model.isNew && model.modelRulesDeducted?.bpo_access && model.modelRulesDeducted?.is_active
                                            ? "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                            : "opacity-40 cursor-not-allowed text-slate-300"
                                    )}
                                    disabled={model.isNew || !model.modelRulesDeducted?.bpo_access || !model.modelRulesDeducted?.is_active}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!model.isNew) {
                                            navigateToPage(SubPages.OperationalModelEditor, model);
                                        }
                                    }}
                                    aria-label="Open Operational Editor"
                                >
                                    <span className="material-symbols-outlined text-[18px]">analytics</span>
                                </button>
                                <div className="absolute bottom-full right-0 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[11px] rounded opacity-0 group-hover/ope:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-10">
                                    {!model.modelRulesDeducted?.is_active ? 'Plan expired' : !model.modelRulesDeducted?.bpo_access ? 'Operational (Upgrade required)' : 'Operational Editor'}
                                </div>
                            </div>
                        </div>

                        {
                            model.editing ?
                            <RenameForm model={model} reloadState={reloadState} isSharedContext={isSharedContext} /> :
                            <p className="px-3 pb-3 text-sm font-bold text-slate-900 truncate group-hover:text-primary transition-colors">{model.name}</p>
                        }

                        <ModelListItemOptionMenu
                            model={model}
                            reloadState={reloadState}
                            state={state}
                            isSharedContext={isSharedContext}
                            userRights={isSharedContext ? userRights : undefined}
                            stateMyModels={stateMyModels}
                            anchorEl={menuAnchorEl}
                            onClose={() => setMenuAnchorEl(null)}
                        />
                    </div>
                </div>
                {isSharedContext
                    ? leaveModal(model, state, reloadState)
                    : deleteModal(model, state, reloadState)
                }
            </div>
        </div>
    )
}
